import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {useTranslation} from '@/translations/i18n'
import {TreatmentRequestExtended} from '@/features/treatment-requests/types'
import {diseasesGroupNameToLabel, extractGroupsDiseaseTree} from '@/features/treatment-requests/utils'
import {formatDate} from '@/dayjs'
import {WarningBanner} from '@/components/commons/warning-banner/WarningBanner'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {DetailsInformationObservedField} from '@/components/commons/details-information-observed-field/DetailsInformationObservedField'
import {DiagnosisCodeLabel} from '@/features/projects/components/diagnosis-code-label/DiagnosisCodeLabel'
import {splitDiagnosisPair} from '@/features/projects/diagnosisVersion'

export const TreatmentRequestDiagnosisDetails = ({treatmentRequest}: {treatmentRequest: TreatmentRequestExtended}) => {
    const {t} = useTranslation()
    const groups = extractGroupsDiseaseTree(treatmentRequest.disease)
    const labelKey = groups.group?.name as keyof typeof diseasesGroupNameToLabel
    const isOnLastStep = treatmentRequest.status == 'accepted' || treatmentRequest.status == 'rejected'

    // Show the "Da compilare" indicator for all non-final statuses, plus any time the backend
    // explicitly flags the field — mirrors the pattern used by other semi-required fields.
    const isDiagnosisCodeRequired = !isOnLastStep || treatmentRequest.fieldsToCompile.includes('diagnosisCode')

    // One code is stored, in whichever classification it was picked: read mode shows both, exactly
    // as the project pathologies do.
    const {icd9, icd10} = splitDiagnosisPair(treatmentRequest.diagnosisCode)

    const renderDiseaseDetails = () => {
        if ((!treatmentRequest.disease || !treatmentRequest.disease.parent) && isOnLastStep) {
            return (
                <WarningBanner
                    maxWidth={'unset'}
                    title={t('treatment_requests:no_disease_available')}
                    subtitle={t('treatment_requests:no_disease_available_subtitle')}
                />
            )
        }

        if (!treatmentRequest.disease) {
            return <WarningBanner maxWidth={'unset'} title={t('treatment_requests:no_disease_available')} />
        }

        if (!treatmentRequest.disease.parent) {
            return (
                <WarningBanner
                    maxWidth={'unset'}
                    title={t('treatment_requests:request_disease_group_not_compiled', {
                        group: treatmentRequest.disease.name
                    })}
                    subtitle={t('treatment_requests:request_disease_group_not_compiled_subtitle')}
                />
            )
        }

        return (
            <Grid columns={3} fullWidth rowGap={4}>
                {treatmentRequest.disease?.parent?.name && (
                    <GridItem spanColumn={3} direction="column" gap={1}>
                        <Text size="textSm" palette="neutral" shade="500">
                            {t('treatment_requests:disease_group_priority')}
                        </Text>
                        <Text size="textMd" weight="500">
                            {t(diseasesGroupNameToLabel[labelKey])}
                        </Text>
                    </GridItem>
                )}

                {groups.subgroups.map(
                    (group, index) =>
                        group?.name && (
                            <GridItem key={index} direction="column" gap={1}>
                                <Text size="textSm" palette="neutral" shade="500">
                                    {t('treatment_requests:disease_group_priority')}
                                </Text>
                                <Text size="textMd" weight="500">
                                    {group.name}
                                </Text>
                            </GridItem>
                        )
                )}

                {treatmentRequest.diseaseOnsetDate && (
                    <GridItem spanColumn={3} direction="column" gap={1}>
                        <Text size="textSm" palette="neutral" shade="500">
                            {t('treatment_requests:onset_date')}
                        </Text>
                        <Text size="textMd" weight="500">
                            {formatDate(treatmentRequest.diseaseOnsetDate, 'DD/MM/YYYY')}
                        </Text>
                    </GridItem>
                )}
                {treatmentRequest.fieldsToCompile.includes('disease') ? (
                    isOnLastStep ? (
                        <GridItem spanColumn={3} direction="column" gap={1}>
                            <WarningBanner
                                maxWidth={'unset'}
                                title={t('treatment_requests:request_disease_sub_group_not_compiled_2', {
                                    group: treatmentRequest.disease.name.toLowerCase()
                                })}
                                subtitle={t('treatment_requests:request_disease_sub_group_not_compiled_subtitle_2')}
                            />
                        </GridItem>
                    ) : (
                        <GridItem spanColumn={3} direction="column" gap={1}>
                            <WarningBanner
                                maxWidth={'unset'}
                                title={t('treatment_requests:request_disease_sub_group_not_compiled', {
                                    group: treatmentRequest.disease.name.toLowerCase()
                                })}
                                subtitle={t('treatment_requests:request_disease_group_not_compiled_subtitle')}
                            />
                        </GridItem>
                    )
                ) : null}
            </Grid>
        )
    }

    return (
        <Flexbox direction="column" gap={4} fullWidth>
            {renderDiseaseDetails()}

            <Grid columns={3} fullWidth rowGap={4}>
                <GridItem spanColumn={3} direction="column" gap={1}>
                    <Text size="textSm" palette="neutral" shade="500">
                        {t('treatment_requests:diagnosis_code')}
                    </Text>
                    {isDiagnosisCodeRequired ? (
                        <DetailsInformationObservedField<'diagnosisCode'>
                            name="diagnosisCode"
                            hideIcons={['success']}
                            value={
                                treatmentRequest.diagnosisCode && (
                                    <Flexbox direction="column" gap={1}>
                                        <DiagnosisCodeLabel diagnosis={icd9} />
                                        <DiagnosisCodeLabel diagnosis={icd10} />
                                    </Flexbox>
                                )
                            }
                        />
                    ) : treatmentRequest.diagnosisCode ? (
                        <Flexbox direction="column" gap={1}>
                            <DiagnosisCodeLabel diagnosis={icd9} />
                            <DiagnosisCodeLabel diagnosis={icd10} />
                        </Flexbox>
                    ) : (
                        <Text size="textMd" weight="500">
                            -
                        </Text>
                    )}
                </GridItem>
            </Grid>
        </Flexbox>
    )
}
